<template>
  <PageSection>
    <SectionBox v-if="currentPlaceholder">
      <BasicSection>
        <SectionTitle title="Ваш отклик" divider />
        <NoContent
          :title="currentPlaceholder.title"
          :description="currentPlaceholder.description"
          :icon="$options.limitedImage"
          :actions="currentPlaceholder.actions || []"
        />
      </BasicSection>
    </SectionBox>
    <ResourceAction
      v-else
      ref="deleteAction"
      :action="deleteAction"
      :params="params"
      @success="onDeleteSuccess"
    >
      <SectionBox>
        <BasicSection v-if="currentResponse" appearance="always-same-paddings">
          <SectionTitle title="Ваш отклик" divider />
          <BasicForm
            :action="saveAction"
            :params="params"
            @success="onSaveSuccess"
          >
            <VacancyResponse
              v-if="editMode"
              :response="currentResponse"
              :has-collapsed-section="false"
            >
              <template #message>
                <LimitWidth max="500">
                  <SectionGroup>
                    <FormField name="body">
                      <BasicTextarea
                        ref="editFormTextarea"
                        v-model="editForm.message"
                        rows="5"
                        max-rows="10"
                        name="body"
                      />
                    </FormField>
                    <ContentSection
                      v-if="fields.email"
                      title="Электронная почта"
                      appearance="form-section"
                    >
                      <FormField name="email">
                        <TextInput name="email" v-model="editForm.email" />
                      </FormField>
                    </ContentSection>
                  </SectionGroup>
                </LimitWidth>
              </template>
              <template #controls>
                <LimitWidth max="500">
                  <ButtonGroup appearance="inline">
                    <BasicButton appearance="primary" size="l" type="submit">
                      Сохранить
                    </BasicButton>
                    <BasicButton
                      appearance="secondary"
                      size="l"
                      @click="leaveEditMode"
                    >
                      Отмена
                    </BasicButton>
                  </ButtonGroup>
                </LimitWidth>
              </template>
            </VacancyResponse>
            <VacancyResponse
              v-else
              :response="currentResponse"
              :has-collapsed-section="false"
            >
              <template #controls>
                <div class="create-vacancy-response__controls">
                  <BasicButton
                    v-if="currentResponse.canUpdate"
                    appearance="muted"
                    size="l"
                    class="create-vacancy-response__button"
                    @click="enterEditMode"
                  >
                    <SvgIcon icon="edit_new" size="24" color="muted" />
                    <span>Редактировать</span>
                  </BasicButton>
                  <BasicButton
                    v-else
                    appearance="muted"
                    size="l"
                    class="create-vacancy-response__button"
                    @click="overlayVisible = true"
                  >
                    <SvgIcon icon="trash" size="18" color="muted" />
                    <span>Удалить</span>
                  </BasicButton>
                  <BasicButton
                    v-if="currentResponse.canUpdate"
                    appearance="muted"
                    size="l"
                    class="create-vacancy-response__button"
                    @click="overlayVisible = true"
                  >
                    <SvgIcon icon="trash" size="18" color="muted" />
                    <span>Удалить</span>
                  </BasicButton>
                </div>
              </template>
            </VacancyResponse>
          </BasicForm>
          <TransitionFade>
            <BasicConfirm
              v-if="overlayVisible"
              title="Удаление отклика"
              @close="overlayVisible = false"
              @confirm="$refs.deleteAction.dispatch()"
            >
              Вы уверены что хотите удалить свой отклик?
            </BasicConfirm>
          </TransitionFade>
        </BasicSection>
        <BasicForm
          v-else
          ref="form"
          :action="submitAction"
          :params="submitParams"
          @success="onSubmitSuccess"
          @error="onSubmitError"
        >
          <BasicSection appearance="always-same-paddings">
            <SectionTitle title="Ваш отклик" divider />
            <SectionGroup gap="larger">
              <ContentSection
                v-if="fields.salary"
                title="Желаемая зарплата"
                appearance="form-section"
              >
                <SectionGroup gap="medium-large">
                  <BasicText size="medium">
                    Укажите, сколько вы&nbsp;хотите зарабатывать&nbsp;&mdash;
                    это выделит ваш отклик среди остальных и&nbsp;поможет эйчару
                    сориентироваться. Мы&nbsp;сохраним эту цифру в&nbsp;вашем
                    профиле в&nbsp;поле &laquo;Ожидаемое вознаграждение&raquo;
                  </BasicText>
                  <InputGroup appearance="response-price">
                    <FormField name="user[salary]">
                      <NumberInput
                        name="user[salary]"
                        v-model="createForm.salary"
                        placeholder="Сумма"
                      />
                    </FormField>
                    <FormField name="user[currency]">
                      <BasicSelect
                        name="user[currency]"
                        :options="options.currencies"
                        v-model="createForm.currency"
                      />
                    </FormField>
                    <InputGroupText v-if="!mediaQueries.mobile">
                      <BasicText size="medium">на руки</BasicText>
                    </InputGroupText>
                  </InputGroup>
                </SectionGroup>
              </ContentSection>
              <ContentSection
                v-if="fields.email"
                title="Электронная почта"
                appearance="form-section"
              >
                <SectionGroup gap="medium-large">
                  <BasicText size="medium">
                    Компания включила обязательный сбор адресов электронной
                    почты в&nbsp;откликах на&nbsp;свои вакансии. Ваш адрес будет
                    передан её&nbsp;представителям и&nbsp;может быть использован
                    при дальнейшем общении.
                  </BasicText>
                  <FormField name="email">
                    <TextInput name="email" v-model="createForm.email" />
                  </FormField>
                  <BasicText v-if="fields.email.suggest" size="medium">
                    Использовать почту по умолчанию:
                    <BasicButton
                      appearance="pseudo-link"
                      size="l"
                      @click="createForm.email = fields.email.suggest"
                    >
                      {{ fields.email.suggest }}
                    </BasicButton>
                  </BasicText>
                </SectionGroup>
              </ContentSection>
              <ContentSection
                title="Сопроводительное письмо"
                appearance="form-section"
              >
                <SectionGroup gap="medium-large">
                  <BasicText size="medium">
                    Работодатель прежде всего смотрит на&nbsp;ваш профиль,
                    но&nbsp;вы&nbsp;также можете сопроводить свой отклик парой
                    слов, чтобы привлечь внимание к&nbsp;своим самым важным
                    профессиональным или личным качествам.
                  </BasicText>
                  <FormField name="body">
                    <BasicTextarea
                      name="body"
                      rows="5"
                      max-rows="10"
                      v-model="createForm.message"
                    />
                  </FormField>
                  <SkillBanner
                    v-if="skillsBanner"
                    :skills-banner="skillsBanner"
                  />
                </SectionGroup>
              </ContentSection>
              <ContentSection
                v-if="captchaVisible"
                title="Капча"
                appearance="form-section"
              >
                <RecaptchaField />
              </ContentSection>
            </SectionGroup>
          </BasicSection>
          <SubmitBox>
            <BasicSection appearance="submit">
              <BasicButton appearance="primary" size="l" type="submit">
                Откликнуться
              </BasicButton>
              <FooterText
                v-if="responsesLeft <= responsesThreshold"
                class="create-vacancy-response__footer-text"
              >
                <BasicText size="sm">
                  {{ responsesLeftPrefix }}
                </BasicText>
                <BasicText size="sm" weight="bold">
                  {{ responsesLeftCount }}. Быстрые отклики не влияют на лимит.
                </BasicText>
                <BasicLink
                  href="/info/help_user#q1.3"
                  title="Подробнее о лимите откликов"
                  appearance="sm"
                  target="_blank"
                  layout="mobile-block"
                />
              </FooterText>
            </BasicSection>
          </SubmitBox>
        </BasicForm>
      </SectionBox>
    </ResourceAction>
  </PageSection>
</template>

<script>
import { VacanciesResponsesResource } from 'src/services/vacancies.js';
import { getRussianPluralDeclension } from 'src/utils/translation.js';

import BasicButton from 'src/components/BasicButton/BasicButton.vue';
import BasicConfirm from 'src/components/BasicConfirm/BasicConfirm.vue';
import BasicLink from 'src/components/BasicLink/BasicLink.vue';
import ButtonGroup from 'src/components/ButtonGroup/ButtonGroup.vue';
import BasicForm from 'src/components/form/BasicForm/BasicForm.vue';
import BasicSelect from 'src/components/form/BasicSelect/BasicSelect.vue';
import BasicTextarea from 'src/components/form/BasicTextarea/BasicTextarea.vue';
import FormField from 'src/components/form/FormField/FormField.vue';
import InputGroup from 'src/components/form/InputGroup/InputGroup.vue';
import NumberInput from 'src/components/form/NumberInput/NumberInput.vue';
import TextInput from 'src/components/form/TextInput/TextInput.vue';
import SvgIcon from 'src/components/icons/SvgIcon/SvgIcon.vue';
import LimitWidth from 'src/components/LimitWidth/LimitWidth.vue';
import OpposingItems from 'src/components/OpposingItems/OpposingItems.vue';
import NoContent from 'src/components/placeholders/NoContent/NoContent.vue';
import RecaptchaField from 'src/components/RecaptchaField/RecaptchaField.vue';
import ResourceAction from 'src/components/ResourceAction/ResourceAction.vue';
import VacancyResponse from 'src/components/responses/VacancyResponse/VacancyResponse.vue';
import SectionBox from 'src/components/SectionBox/SectionBox.vue';
import BasicSection from 'src/components/sections/BasicSection/BasicSection.vue';
import ContentSection from 'src/components/sections/ContentSection/ContentSection.vue';
import PageSection from 'src/components/sections/PageSection/PageSection.vue';
import SectionGroup from 'src/components/sections/SectionGroup/SectionGroup.vue';
import SectionTitle from 'src/components/sections/SectionTitle/SectionTitle.vue';
import SkillBanner from 'src/components/SkillBanner/SkillBanner.vue';
import SubmitBox from 'src/components/SubmitBox/SubmitBox.vue';
import BasicText from 'src/components/text/BasicText/BasicText.vue';
import FooterText from 'src/components/text/FooterText/FooterText.vue';
import InputGroupText from 'src/components/text/InputGroupText/InputGroupText.vue';
import TransitionFade from 'src/components/transitions/TransitionFade/TransitionFade.vue';

import limitedImage from 'images/no_content.icon.response.svg';

const responsesLeftPrefixDeclension = getRussianPluralDeclension(
  'У вас остался',
  'У вас осталось',
  'У вас осталось',
);

const responsesLeftCountDeclension = getRussianPluralDeclension(
  '${0} отклик',
  '${0} отклика',
  '${0} откликов',
);

export default {
  name: 'CreateVacancyResponse',
  components: {
    PageSection,
    InputGroupText,
    InputGroup,
    BasicSelect,
    NumberInput,
    RecaptchaField,
    TextInput,
    NoContent,
    OpposingItems,
    BasicConfirm,
    ResourceAction,
    ButtonGroup,
    TransitionFade,
    VacancyResponse,
    LimitWidth,
    SectionGroup,
    BasicText,
    FooterText,
    BasicLink,
    ContentSection,
    BasicButton,
    SubmitBox,
    BasicTextarea,
    FormField,
    SectionTitle,
    SectionBox,
    BasicSection,
    BasicForm,
    SkillBanner,
    SvgIcon,
  },
  inject: ['mediaQueries'],

  limitedImage: {
    src: limitedImage,
    width: 86,
    height: 98,
  },

  props: {
    placeholder: Object,
    fields: Object,
    response: Object,
    vacancyId: Number,
    responsesThreshold: Number,
    responsesLeft: Number,
    currencies: Array,
    showTrackTest: Boolean,
    skillsBanner: Object,
  },
  data() {
    const { fields = {}, currencies = [] } = this;
    const {
      message: { value: message } = {},
      email: { value: email } = {},
      salary: { currency, value: salary } = {},
    } = fields;
    return {
      currentResponse: this.response,
      currentPlaceholder: this.placeholder,
      createForm: {
        message,
        email,
        salary,
        currency,
      },
      editForm: null,
      editMode: false,
      overlayVisible: false,
      captchaVisible: false,
      options: {
        currencies,
      },
    };
  },
  methods: {
    submitAction: VacanciesResponsesResource.post,
    saveAction: VacanciesResponsesResource.patch,
    deleteAction: VacanciesResponsesResource.delete,
    enterEditMode() {
      this.editMode = true;
      const { message, email } = this.currentResponse;
      this.editForm = { message, email };
      this.$nextTick(() => {
        this.$refs.editFormTextarea.focus();
      });
    },
    leaveEditMode() {
      this.editMode = false;
    },
    onSubmitSuccess({ response }) {
      this.currentResponse = response;
      this.captchaVisible = false;
      this.responsesLeft -= 1;
    },
    onSubmitError({
      response: {
        data: { error },
      },
    }) {
      if (error.type === 'captcha') this.captchaVisible = true;
    },
    onSaveSuccess({ response }) {
      this.editMode = false;
      if (response) this.currentResponse = response;
    },
    onDeleteSuccess({ data }) {
      if (data.placeholder) this.currentPlaceholder = data.placeholder;
      this.overlayVisible = false;
      this.currentResponse = null;
    },
  },
  computed: {
    params() {
      const { currentResponse, vacancyId } = this;
      if (!currentResponse) return null;
      const { id: responseId } = currentResponse;
      return { vacancyId, responseId };
    },
    submitParams() {
      const { vacancyId } = this;
      return { vacancyId };
    },
    responsesLeftPrefix() {
      return responsesLeftPrefixDeclension(this.responsesLeft);
    },
    responsesLeftCount() {
      return responsesLeftCountDeclension(this.responsesLeft).replace(
        '${0}',
        this.responsesLeft,
      );
    },
  },
};
</script>
